import {
  customerCreate,
  customerCreateBranch,
  customerCreateContact,
  customerEditContact,
  customerGet,
  customerGetAllProductsWithOverrides,
  customerGetCompany,
  customerGetContact,
  customerGetProcessingLevels,
  customerGetProductOverrides,
  customerListBranchCompanies,
  customerListContacts,
  customerUpdateAddresses,
  customerUpdateBranch,
  customerUpdateBranchAddress,
  customerUpdateCommerceIntegrationSettings,
  customerUpdateCustomerRequoteDetails,
  customerUpdateDeliveryDays,
  customerUpdateFinancialSettings,
  customerUpdateProductOverrides,
  customerUpdateRaw,
  customerUpdateUsers,
  customerUpdateVendorAddress,
  customerUploadDocumentWithUrl,
  staffAddTaxRate,
  staffDeleteTaxRate,
  staffGetCustomerDiscounts,
  staffGetDiscountList,
  staffGetStaffContact,
  staffGetTaxRates,
  staffListCustomers,
  staffListStaff,
  staffUpdateDiscounts,
  staffUpdateTaxRates
} from '@/api'
import { useAuthStore } from '@/pinia/auth'
import { config } from '@/utils/config'
import { defineStore } from 'pinia'

export const useCustomerStore = defineStore('customer', {
  persist: true,
  state: () => ({
    customer: undefined,
    lastUpdated: undefined
  }),
  actions: {
    setCustomer(customer) {
      this.customer = customer
    },
    resetCustomer() {
      this.customer = undefined
    },
    async setLastUpdated(lastUpdated) {
      this.lastUpdated = lastUpdated
    },
    async customerGet(customerId) {
      const authStore = useAuthStore()
      return customerGet(customerId, authStore.staffLogin)
    },
    async customerUpdateRaw(customer) {
      return customerUpdateRaw(customer)
    },
    async customerBranchUpdate(branch) {
      return customerUpdateBranch(branch)
    },
    async branchUpdateAddress(address) {
      return customerUpdateBranchAddress(address)
    },
    async updateCustomerRequoteDetails(customer) {
      return customerUpdateCustomerRequoteDetails(customer)
    },
    async customerUpdateDeliveryDays(customer) {
      const authStore = useAuthStore()
      return customerUpdateDeliveryDays(customer, authStore.staffLogin)
    },
    async customerUpdateUsers({ customer, usersIds }) {
      const authStore = useAuthStore()
      return customerUpdateUsers(customer, authStore.staffLogin, usersIds)
    },
    async customerCreateUsers({ customer }) {
      const authStore = useAuthStore()
      return customerUpdateUsers(customer, authStore.staffLogin)
    },
    async customerUpdateAddresses({ customer }) {
      const authStore = useAuthStore()
      return customerUpdateAddresses(customer, authStore.staffLogin)
    },
    async customerUpdateVendorAddress(customer) {
      const authStore = useAuthStore()
      return customerUpdateVendorAddress(customer, authStore.staffLogin)
    },
    async customerCreate(customer) {
      return customerCreate(customer)
    },
    async customerCreateBranch(branch) {
      return customerCreateBranch(branch)
    },
    async customerListContacts() {
      return customerListContacts()
    },
    async customerGetContact(ContactId) {
      return customerGetContact(ContactId)
    },
    async customerEditContact(Contact) {
      return customerEditContact(Contact)
    },
    async customerCreateContact(Contact) {
      return customerCreateContact(Contact)
    },
    async staffListCustomers(options) {
      return staffListCustomers(
        options.limit,
        options.offset,
        options.search,
        undefined,
        undefined,
        options.customerStatus,
        options.generalLicenseStatus,
        options.branchCompanyId,
        options.customerLevel
      )
    },
    async staffListStaff({ options, branchCompanyId }) {
      return staffListStaff(options.limit, options.offset, branchCompanyId)
    },
    async customerListBranches(options) {
      return customerListBranchCompanies(options.limit, options.offset, options.search)
    },
    async staffGetTaxRates() {
      return staffGetTaxRates()
    },
    async staffGetStaffContact(ContactId) {
      return staffGetStaffContact(ContactId)
    },
    async staffUpdateTaxRates(TaxRates) {
      return staffUpdateTaxRates(TaxRates)
    },
    async staffAddTaxRate(TaxRate) {
      return staffAddTaxRate(TaxRate)
    },
    async staffDeleteTaxRate(id) {
      return staffDeleteTaxRate(id)
    },
    async formatCustomerUpdateFinancialSettings({ vatEnabled, taxValue, fittingRate, currencyId }) {
      return {
        Financial: {
          vatEnabled: Boolean(vatEnabled),
          taxValue: Number(taxValue),
          fittingRate: Number(fittingRate),
          currencyId: currencyId ? Number(currencyId) : undefined
        }
      }
    },
    async formatCustomerUpdateCommerceIntegrationSettings({ eCommerceIntegrationSettings }) {
      return {
        ECommerceIntegration: {
          Alias: eCommerceIntegrationSettings.alias,
          WebAddress: eCommerceIntegrationSettings.webAddress,
          Email: eCommerceIntegrationSettings.email,
          mobilePhone: eCommerceIntegrationSettings.mobilePhone,
          privacyPolicyDocumentId: eCommerceIntegrationSettings.privacyPolicyDocumentId,
          addressId: eCommerceIntegrationSettings.addressId,
          companyRegNumber: eCommerceIntegrationSettings.companyRegNumber,
          fensaRegistrationNumber: eCommerceIntegrationSettings.fensaRegistrationNumber,
          vatRegistrationNumber: eCommerceIntegrationSettings.vatRegistrationNumber,
          priceDisplayMode: Number(eCommerceIntegrationSettings.priceDisplayMode)
        }
      }
    },
    async formatCustomerUpdateLogo({ logo }) {
      return {
        Styling: {
          logo
        }
      }
    },
    async customerUpdateFinancialSettings({ vatEnabled, taxValue, fittingRate }) {
      return customerUpdateFinancialSettings({ vatEnabled, taxValue, fittingRate })
    },
    async customerUpdateCommerceIntegrationSettings({ eCommerceIntegrationSettings, customerId }) {
      return customerUpdateCommerceIntegrationSettings(eCommerceIntegrationSettings, customerId)
    },
    async customerGetProductOverrides() {
      return customerGetProductOverrides()
    },
    async customerGetAllProductsWithOverrides(customerId) {
      return customerGetAllProductsWithOverrides(customerId)
    },
    async customerUpdateProductOverrides(overrides) {
      return customerUpdateProductOverrides(overrides)
    },
    async customerUploadDocumentWithUrl(document) {
      return customerUploadDocumentWithUrl(document)
    },
    async staffGetDiscountList(datasetId) {
      return staffGetDiscountList(datasetId)
    },
    async staffGetCustomerDiscounts({ customerId, datasetId }) {
      return staffGetCustomerDiscounts(customerId, datasetId)
    },
    async staffUpdateDiscounts({ customerId, datasetId, discounts }) {
      return staffUpdateDiscounts(customerId, datasetId, discounts)
    },
    async customerGetProcessingLevels() {
      return customerGetProcessingLevels()
    },
    async customerGetCompany(companyId) {
      return customerGetCompany(companyId)
    }
  },
  getters: {
    getCustomer(state) {
      return state.customer
    },
    requoteMarkup(state) {
      try {
        return state.customer.financial.requoteMarkup
      } catch {
        return 0
      }
    },
    hasBrandedPricingOn(state) {
      try {
        return state.customer.eCommerceIntegration.priceDisplayMode === 0
      } catch {
        return false
      }
    },
    customer_logo(state) {
      try {
        return `${config.image_base_url}${state.customer.styling.logo}`
      } catch {
        // @todo log the error here
        return ''
      }
    },
    customer_name(state) {
      try {
        return `${state.customer.company.name}`
      } catch {
        // @todo log the error here
        return ''
      }
    },
    isBrandedModeEnabled(state) {
      const authStore = useAuthStore()
      if (authStore.isPortal) {
        return state.customer?.eCommerceIntegration?.enabled
      }
      return false
    }
  }
})
